import { useCallback } from "react";
import { ICellRendererParams } from "ag-grid-community";
import { useHistory } from "react-router-dom";
import { Flex, Text, HelpIndicator } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { useCurrentCaseContext } from "~/pages/Compliance/context/CurrentCaseContext";
import { useIOReusableProfiles } from "~/pages/Compliance/flags";
import { OwnersAndControllersRowItem, ProposedChange } from "../../types";
import { container, iconContainer, icon, textColors } from "./styles_vanilla.css";

export const ProposedChangeCellRenderer = ({
  value,
}: ICellRendererParams<OwnersAndControllersRowItem, ProposedChange>) => {
  const history = useHistory();
  const { caseBasePath } = useCurrentCaseContext();
  const ioReusableProfilesEnabled = useIOReusableProfiles();

  const handleIconClick = useCallback(() => {
    const base = `${caseBasePath}/profiles/${value?.sourceProfileId}/proposed-changes/${value?.targetProfileId}`;
    if (ioReusableProfilesEnabled) {
      const query =
        value?.latestVersionId != null ? `?latestVersionId=${value.latestVersionId}` : "";
      history.push(`${base}/review${query}`);
      return;
    }
    history.push(base);
  }, [
    value?.sourceProfileId,
    value?.targetProfileId,
    value?.latestVersionId,
    history,
    caseBasePath,
    ioReusableProfilesEnabled,
  ]);

  if (!value) {
    return <Text className={textColors.neutral}>None</Text>;
  }

  const isDivergedFromReview =
    value.latestVersionId != null && value.latestVersionId !== value.reviewedProfileId;
  const textClass = isDivergedFromReview ? textColors.danger : textColors.informative;
  const iconColor = isDivergedFromReview ? Colors.Danger600 : Colors.Informative600;

  return (
    <Flex
      alignItems="center"
      justifyContent="space-between"
      className={container}
      data-testid="proposed-change-cell-container"
    >
      <Text className={textClass}>Yes</Text>
      <Flex alignItems="center" className={iconContainer}>
        <HelpIndicator id="view-changes-tooltip" placement="top" content="View changes">
          <Icon
            type="search"
            variant="outline"
            color={iconColor}
            onClick={handleIconClick}
            className={icon}
            data-testid="view-changes-icon"
          />
        </HelpIndicator>
      </Flex>
    </Flex>
  );
};
